import { createContext, useContext, useMemo } from 'react'
import * as React from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'

interface DrawerAskAIChatStateValue {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
}

interface DrawerAskAIProviderProps extends DrawerAskAIChatStateValue {
  children: React.ReactNode
}

const DrawerAskAIContext = createContext<DrawerAskAIChatStateValue | undefined>(undefined)

/**
 * Provides shared chat state to modal/mobile DrawerAskAIFooter instances.
 *
 * Mobile drawers still need a drawer-local Ask AI composer because full-screen
 * drawers cover the main chat composer. Desktop right-panel drawers use the
 * main chat composer context instead.
 *
 * ⚠️ Performance: `messages` is a high-frequency value that updates on every
 * streamed token. This is acceptable because:
 * - Consumer count is low and typically only one drawer is visible.
 * - Each consumer's `useDrawerAskAI` hook derives state via `useMemo`.
 */
export function DrawerAskAIProvider({
  children,
  submitMessage,
  messages,
  isStreaming,
}: DrawerAskAIProviderProps) {
  const value = useMemo<DrawerAskAIChatStateValue>(() => ({
    submitMessage,
    messages,
    isStreaming,
  }), [submitMessage, messages, isStreaming])

  return (
    <DrawerAskAIContext.Provider value={value}>
      {children}
    </DrawerAskAIContext.Provider>
  )
}

/**
 * Access shared chat state for modal/mobile drawer Ask AI functionality.
 *
 * Must be used within a DrawerAskAIProvider — throws if the provider
 * is missing so the error surfaces immediately during development.
 */
export function useDrawerAskAIChatState(): DrawerAskAIChatStateValue {
  const context = useContext(DrawerAskAIContext)
  if (!context) {
    throw new Error('useDrawerAskAIChatState must be used within a DrawerAskAIProvider')
  }
  return context
}
